'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Loader2, ChevronDown } from 'lucide-react';

interface StudentFormData {
  username: string;
  password: string;
  ageGroup: string;
  rememberMe: boolean;
}

const ageGroups = ['6–9 (Foundations)', '10–13 (Intermediate)', '14–17 (Advanced)', '18+ (Professional)'];

export default function StudentLoginForm({ isSignUp }: { isSignUp: boolean }) {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const router = useRouter();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<StudentFormData>({ defaultValues: { username: '', password: '', ageGroup: '', rememberMe: false } });

  const onSubmit = async (_data: StudentFormData) => {
    setLoading(true);
    // BACKEND INTEGRATION: POST /api/auth/student with username, password, ageGroup
    await new Promise((r) => setTimeout(r, 1200));
    setLoading(false);
    router.push('/student-dashboard');
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
      <div>
        <label htmlFor="student-username" className="block text-sm font-semibold text-foreground mb-1.5">
          Username or Email
        </label>
        <input
          id="student-username"
          type="text"
          {...register('username', { required: 'Username is required' })}
          placeholder="amara.osei"
          className="w-full px-4 py-2.5 bg-input border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring transition-all"
        />
        {errors.username && <p className="text-xs text-red-500 mt-1">{errors.username.message}</p>}
      </div>

      {isSignUp && (
        <div>
          <label htmlFor="student-age" className="block text-sm font-semibold text-foreground mb-1.5">
            Age Group
          </label>
          <p className="text-xs text-muted-foreground mb-2">This determines which campus wing you start in.</p>
          <div className="relative">
            <select
              id="student-age"
              {...register('ageGroup', { required: 'Please select your age group' })}
              className="w-full px-4 py-2.5 bg-input border border-border rounded-lg text-sm text-foreground outline-none focus:ring-2 focus:ring-ring appearance-none transition-all"
            >
              <option value="">Select your age group...</option>
              {ageGroups.map((ag) => (
                <option key={`age-${ag}`} value={ag}>{ag}</option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>
          {errors.ageGroup && <p className="text-xs text-red-500 mt-1">{errors.ageGroup.message}</p>}
        </div>
      )}

      <div>
        <label htmlFor="student-password" className="block text-sm font-semibold text-foreground mb-1.5">
          Password
        </label>
        <div className="relative">
          <input
            id="student-password"
            type={showPassword ? 'text' : 'password'}
            {...register('password', { required: 'Password is required', minLength: { value: 6, message: 'Minimum 6 characters' } })}
            placeholder="••••••••"
            className="w-full px-4 py-2.5 pr-10 bg-input border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring transition-all"
          />
          <button
            type="button"
            onClick={() => setShowPassword((p) => !p)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password.message}</p>}
      </div>

      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" {...register('rememberMe')} className="w-4 h-4 accent-primary rounded" />
          <span className="text-sm text-muted-foreground">Remember me</span>
        </label>
        <button type="button" className="text-sm text-primary hover:underline font-medium">
          Forgot password?
        </button>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-primary-foreground font-semibold rounded-lg btn-scale hover:bg-primary/90 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
        style={{ minHeight: '44px' }}
      >
        {loading ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            <span>Entering Campus...</span>
          </>
        ) : (
          isSignUp ? 'Create Student Account' : 'Enter Campus'
        )}
      </button>
    </form>
  );
}